import React from 'react';
import { MousePointer2 } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { IPR_TOOL_SELECT, TOOTH_MARKERS } from '../../../lib/iprToothData';

/**
 * Barre d’outils « peindre » : choisir un critère, puis cliquer les dents.
 */
export default function ToothMarkerToolbar({ activeTool, onToolChange, disabled = false }) {
  return (
    <div
      className="mb-4 rounded-xl border border-border bg-muted/20 p-3 sm:p-4"
      role="toolbar"
      aria-label="Outils de marquage dentaire"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-3">
        <div>
          <p className="text-sm font-semibold text-foreground">Marquage clinique</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Choisissez un critère, puis cliquez sur les dents pour l’appliquer ou le retirer.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onToolChange(IPR_TOOL_SELECT)}
          aria-pressed={activeTool === IPR_TOOL_SELECT}
          className={cn(
            'inline-flex min-h-[44px] items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors cursor-pointer',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            activeTool === IPR_TOOL_SELECT
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border bg-card text-foreground hover:bg-muted/60',
          )}
        >
          <MousePointer2 className="w-4 h-4 shrink-0" aria-hidden />
          Sélection IPR
        </button>

        {TOOTH_MARKERS.map((marker) => {
          const isActive = activeTool === marker.key;
          return (
            <button
              key={marker.key}
              type="button"
              disabled={disabled}
              onClick={() => onToolChange(marker.key)}
              aria-pressed={isActive}
              title={marker.description}
              className={cn(
                'inline-flex min-h-[44px] items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors cursor-pointer',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                isActive
                  ? 'border-foreground/30 bg-foreground/5 text-foreground shadow-sm'
                  : 'border-border bg-card text-foreground hover:bg-muted/60',
              )}
            >
              <span
                className={cn(
                  'inline-flex h-6 w-6 items-center justify-center rounded-md text-[11px] font-bold',
                  marker.badgeClass,
                )}
                aria-hidden
              >
                {marker.shortLabel}
              </span>
              <span className="whitespace-nowrap">{marker.label}</span>
            </button>
          );
        })}
      </div>

      {activeTool !== IPR_TOOL_SELECT && (
        <p className="mt-3 text-xs text-muted-foreground">
          Mode actif :{' '}
          <span className="font-semibold text-foreground">
            {TOOTH_MARKERS.find((m) => m.key === activeTool)?.label}
          </span>
          {' — '}cliquez une dent pour basculer.
        </p>
      )}
    </div>
  );
}
